import { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { startEnrollment, verifiedFactorId, verifyCode } from '../../lib/mfa';
import { Button, Icon, Input } from '../ui';

/**
 * Stands in for the whole app until this session has passed the code step (src/lib/mfa.ts).
 * No authenticator yet: scan a QR code (1Password reads it straight off the screen) and confirm one code.
 * Authenticator on file: enter the current code. Either way the session becomes aal2 and the app loads.
 */
export default function TwoFactorGate() {
  const { mfaState, refreshMfa, logout } = useUser();
  const [enrolling, setEnrolling] = useState<{ factorId: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  // First sign-in after this shipped: start enrollment straight away so the QR code is waiting.
  useEffect(() => {
    if (mfaState !== 'enroll' || enrolling) return;
    let cancelled = false;
    startEnrollment()
      .then((e) => !cancelled && setEnrolling(e))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Could not start two-factor setup'));
    return () => {
      cancelled = true;
    };
  }, [mfaState, enrolling]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError('');
    try {
      const factorId = mfaState === 'enroll' ? enrolling?.factorId : await verifiedFactorId();
      if (!factorId) throw new Error('No authenticator found for this account');
      await verifyCode(factorId, code);
      setCode('');
      await refreshMfa();
    } catch (err) {
      setError(err instanceof Error && /invalid|expired/i.test(err.message) ? 'That code didn’t match. Try the next one.' : err instanceof Error ? err.message : 'Could not check the code');
    } finally {
      setBusy(false);
    }
  };

  const title = mfaState === 'enroll' ? 'Set up two-factor' : 'Enter your code';
  const subtitle =
    mfaState === 'enroll'
      ? 'Cockpit now needs a code from an authenticator app at every sign-in. Scan this with 1Password or your authenticator app, then enter the 6-digit code it shows.'
      : 'Open your authenticator app and enter the 6-digit code for Cockpit.';

  return (
    <div className="min-h-[calc(100dvh-var(--sat))] flex items-center justify-center p-4" style={{ background: 'var(--surface-canvas)' }}>
      <div
        className="w-full p-6 md:p-[32px]"
        style={{
          maxWidth: '420px',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        <div className="flex flex-col items-center text-center" style={{ marginBottom: 'var(--space-6)' }}>
          <img src="/bullfinch-mark.svg" alt="Bullfinch" style={{ width: '44px', height: '44px', marginBottom: 'var(--space-4)' }} />
          <h1 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 600, letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
            {mfaState === 'checking' ? 'Cockpit' : mfaState === 'error' ? 'Couldn’t check two-factor' : title}
          </h1>
          {mfaState === 'enroll' || mfaState === 'verify' ? (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{subtitle}</p>
          ) : null}
        </div>

        {mfaState === 'checking' ? (
          <p className="t-eyebrow text-center" style={{ color: 'var(--text-faint)', margin: 0 }}>
            Checking sign-in…
          </p>
        ) : mfaState === 'error' ? (
          <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', textAlign: 'center' }}>
              Cockpit stays locked until it can confirm this sign-in.
            </p>
            <Button variant="primary" onClick={() => refreshMfa()}>
              Try again
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
            {mfaState === 'enroll' ? (
              enrolling ? (
                <div className="flex flex-col items-center" style={{ gap: 'var(--space-3)' }}>
                  {/* White tile: the QR needs contrast in dark mode. */}
                  <img src={enrolling.qr} alt="QR code for your authenticator app" style={{ width: '176px', height: '176px', background: '#fff', padding: '8px', borderRadius: 'var(--radius-md)' }} />
                  {showSecret ? (
                    <code className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-body)', wordBreak: 'break-all', textAlign: 'center' }}>
                      {enrolling.secret}
                    </code>
                  ) : (
                    <button type="button" onClick={() => setShowSecret(true)} style={{ background: 'none', border: 'none', color: 'var(--accent-text)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}>
                      Can’t scan? Show the setup key
                    </button>
                  )}
                </div>
              ) : (
                <p className="t-eyebrow text-center" style={{ color: 'var(--text-faint)', margin: 0 }}>
                  Preparing your QR code…
                </p>
              )
            ) : null}

            {error ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '9px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--status-down-soft)',
                  color: 'var(--danger-500)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                <Icon name="circle-alert" size={15} />
                {error}
              </div>
            ) : null}

            <Input
              label="6-digit code"
              icon="shield-check"
              fieldSize="lg"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              autoFocus
            />
            <Button type="submit" variant="primary" size="lg" disabled={busy || code.length !== 6 || (mfaState === 'enroll' && !enrolling)}>
              {busy ? 'Checking…' : mfaState === 'enroll' ? 'Turn on two-factor' : 'Continue'}
            </Button>
          </form>
        )}

        <div className="text-center" style={{ marginTop: 'var(--space-5)' }}>
          <button type="button" onClick={logout} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
